import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import ContactInfoForm from "../components/ContactInfoForm";
import BirthEventForm from "../components/BirthEventForm";
import PrenatalVisitForm from '../components/PrenatalVisitForm';
import { 
    Toast,
    useToast, 
    useFormState, 
    useLoadingState, 
    useCommonNavigation,
    clientApi,
    API_BASE_URL 
} from '../utils';

const TABS = [
  { key: "contact", label: "Contact Information" },
  { key: "summary", label: "Pregnancy Summary" },
  { key: "vitals", label: "Vitals" },
  { key: "visits", label: "Routine Visits" },
  { key: "labs", label: "Labs" },
  { key: "birth", label: "Birth Events" },
];

export default function ClientChartTabs() {
  const location = useLocation();
  const { clientId } = useParams();
  const { goToClients, goToDashboard } = useCommonNavigation();
  const { toastMessage, showToast } = useToast();
  const { isLoading, setIsLoading, error, setError } = useLoadingState(true);
  
  // Format date fields to strip time component
  const formatDateFields = (data) => {
    const dateFields = ['DateOfBirth', 'DueDate'];
    const formatted = { ...data };
    dateFields.forEach(field => {
      if (formatted[field]) {
        formatted[field] = formatted[field].split('T')[0];
      }
    });
    return formatted;
  };

  // Client state management
  const rawClient = location.state?.client || null;
  const normalizedClient = rawClient
    ? {
        id: rawClient.id || rawClient.Id,
        firstName: rawClient.firstName || rawClient.FirstName || '',
        lastName: rawClient.lastName || rawClient.LastName || '',
        dateOfBirth: (rawClient.dateOfBirth || rawClient.DateOfBirth || '').split('T')[0],
        dueDate: (rawClient.dueDate || rawClient.DueDate || '').split('T')[0],
        gravida: rawClient.gravida || rawClient.Gravida,
        para: rawClient.para || rawClient.Para,
        allergies: rawClient.allergies || rawClient.Allergies || '',
        clientType: rawClient.clientType || rawClient.ClientType,
        clientStatus: rawClient.clientStatus || rawClient.ClientStatus,
        user: { id: rawClient.userId || rawClient.User?.Id || null },
        midwife: { id: rawClient.midwifeId || rawClient.Midwife?.Id || null },
        babies: [],
        chartEntries: [],
        pregnancies: []
      }
    : null;
  const [client, setClient] = useState(normalizedClient);
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [activeTab, setActiveTab] = useState("summary");
  
  // Pregnancy management
  const [pregnancies, setPregnancies] = useState([]);
  const [selectedPregnancy, setSelectedPregnancy] = useState(null);
  const { form: newPregnancyForm, handleChange: handlePregnancyChange, setForm: setNewPregnancyForm } = useFormState({
    dueDate: "",
    notes: "",
    isTwins: false,
    gravida: client?.gravida || 0,
    para: client?.para || 0
  });
  const [showDueDateModal, setShowDueDateModal] = useState(false);

  // Baby management
  const [babies, setBabies] = useState([]);
  const { form: babyForm, handleChange: handleBabyChange, setForm: setBabyForm } = useFormState({
    name: "",
    birthWeight: "",
    notes: "",
    isTwin: false,
    twinOrder: null,
    birthType: "Single"
  });
  const [babySaveStatus, setBabySaveStatus] = useState("saved");
  const [inviteStatus, setInviteStatus] = useState("idle");
  const [activeBabyIndex, setActiveBabyIndex] = useState(0);

  // Visit management
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [isNewVisit, setIsNewVisit] = useState(false);

  // Handle changes for the active baby
  const handleActiveBabyChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for birth type changes
    if (name === 'birthType') {
      if (value === 'Twin') {
        // Create or update first twin
        const firstBaby = babies.length > 0 
          ? { ...babies[0], isTwin: true, twinOrder: 1, birthType: 'Twin' }
          : { 
              isTwin: true, 
              twinOrder: 1, 
              birthType: 'Twin',
              firstName: '',
              lastName: '',
              dateOfBirth: '',
              birthPlace: ''
            };

        // Create second twin
        const secondBaby = {
          isTwin: true,
          twinOrder: 2,
          birthType: 'Twin',
          dateOfBirth: firstBaby.dateOfBirth || "",
          birthPlace: firstBaby.birthPlace || "",
          lastName: firstBaby.lastName || ""
        };

        // Update babies array with both twins
        setBabies([firstBaby, secondBaby]);
        setActiveBabyIndex(0); // Start with first twin
        return;
      } else {
        // If changing from twin to single/breach, keep only the first baby
        if (babies.length > 1) {
          const updatedBaby = { ...babies[0], isTwin: false, twinOrder: null, birthType: value };
          setBabies([updatedBaby]);
          setActiveBabyIndex(0);
          return;
        }
      }
    }

    if (babies.length > 0) {
      // Update the baby in the babies array
      setBabies(prev => prev.map((baby, index) => 
        index === activeBabyIndex 
          ? { ...baby, [name]: value }
          : baby
      ));
    } else {
      // Update the babyForm for a new baby
      handleBabyChange(e);
    }
  };

  // Fetch client data
  useEffect(() => {
    if (normalizedClient) return;

    const fetchClient = async () => {
      try {
        const data = await clientApi.fetchClient(clientId);
        // Format the data with consistent casing and preserve all fields
        const formattedData = {
          ...data, // Keep all original fields
          id: data.id || data.Id,
          firstName: data.firstName || data.FirstName || '',
          lastName: data.lastName || data.LastName || '',
          dateOfBirth: (data.dateOfBirth || data.DateOfBirth || '').split('T')[0],
          dueDate: (data.dueDate || data.DueDate || '').split('T')[0],
          gravida: data.gravida || data.Gravida,
          para: data.para || data.Para,
          allergies: data.allergies || data.Allergies || '',
          clientType: data.clientType || data.ClientType,
          clientStatus: data.clientStatus || data.ClientStatus,
          user: { id: data.userId || data.User?.Id || null },
          midwife: { id: data.midwifeId || data.Midwife?.Id || null },
          babies: data.Babies || [],
          chartEntries: data.ChartEntries || [],
          pregnancies: data.Pregnancies || []
        };
        console.log('Fetched and formatted client data:', formattedData);
        setClient(formattedData);
        // Initialize babies state from fetched data
        if (formattedData.babies && formattedData.babies.length > 0) {
          setBabies(formattedData.babies);
        }
      } catch (err) {
        console.error('Error fetching client:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClient();
  }, [clientId, normalizedClient]);

  // Fetch pregnancies
  useEffect(() => {
    const fetchPregnancies = async () => {
      try {
        const data = await clientApi.fetchPregnancies(clientId);
        setPregnancies(data);
        setSelectedPregnancy(data[0] || null);
      } catch (err) {
        console.error('Pregnancy fetch error:', err);
        setError(err.message || "Failed to fetch pregnancies");
      }
    };

    if (client) fetchPregnancies();
  }, [client, clientId]);

  // Fetch visits when pregnancy changes
  useEffect(() => {
    const fetchVisits = async () => {
      if (!selectedPregnancy?.id) return;
      
      try {
        const data = await clientApi.fetchVisits(selectedPregnancy.id);
        // Add complete client data to the pregnancy data for each visit
        const visitsWithClientData = data.map(visit => ({
          ...visit,
          pregnancy: {
            ...selectedPregnancy,
            Client: {
              Id: client.id,
              FirstName: client.firstName,
              LastName: client.lastName,
              User: {
                Id: client.user?.id,
                FirstName: client.firstName,
                LastName: client.lastName,
                Email: client.email || `${client.firstName}.${client.lastName}@example.com`.toLowerCase(),
                PasswordHash: "placeholder"
              },
              Pregnancies: [],
              Appointments: [],
              ChartEntries: []
            }
          }
        }));
        setVisits(visitsWithClientData);
      } catch (err) {
        console.error('Error fetching visits:', err);
        showToast('Error loading visits', 'error');
      }
    };

    if (client && selectedPregnancy) {
      fetchVisits();
    }
  }, [selectedPregnancy, client]);

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    
    // If it's a date field and the value includes a time component, strip it out
    const formattedValue = e.target.type === 'date' && value.includes('T') 
      ? value.split('T')[0] 
      : value;
    
    // Convert the field name to camelCase
    const camelCase = name.charAt(0).toLowerCase() + name.slice(1);
    
    setClient(prev => ({
      ...prev,
      [camelCase]: formattedValue
    }));
    setIsDirty(true);
  };

  const saveClientChanges = async () => {
    try {
      setSaveStatus("saving");
      const response = await clientApi.updateClient(clientId, client);
      if (response) {
        // Properly format the response data while preserving all fields
        const formattedResponse = {
          ...response,  // Keep all original fields
          id: response.id || response.Id,
          firstName: response.firstName || response.FirstName || '',
          lastName: response.lastName || response.LastName || '',
          dateOfBirth: (response.dateOfBirth || response.DateOfBirth || '').split('T')[0],
          dueDate: (response.dueDate || response.DueDate || '').split('T')[0],
          gravida: response.gravida || response.Gravida,
          para: response.para || response.Para,
          allergies: response.allergies || response.Allergies || '',
          clientType: response.clientType || response.ClientType,
          clientStatus: response.clientStatus || response.ClientStatus,
          user: { id: response.userId || response.User?.Id || null },
          midwife: { id: response.midwifeId || response.Midwife?.Id || null },
          // Preserve existing collections if not in response
          babies: response.Babies || client.babies || [],
          chartEntries: response.ChartEntries || client.chartEntries || [],
          pregnancies: response.Pregnancies || client.pregnancies || []
        };
        setClient(formattedResponse);
      }
      setSaveStatus("saved");
      setIsDirty(false);
      showToast("Changes saved successfully");
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus("error");
      showToast("Failed to save changes", "error");
    }
  };

  useEffect(() => {
    if (!isDirty) return;

    const timeoutId = setTimeout(async () => {
      try {
        setSaveStatus("saving");
        const response = await clientApi.updateClient(clientId, client);
        if (response) {
          // Properly format the response data while preserving all fields
          const formattedResponse = {
            ...response,  // Keep all original fields
            id: response.id || response.Id,
            firstName: response.firstName || response.FirstName || '',
            lastName: response.lastName || response.LastName || '',
            dateOfBirth: (response.dateOfBirth || response.DateOfBirth || '').split('T')[0],
            dueDate: (response.dueDate || response.DueDate || '').split('T')[0],
            gravida: response.gravida || response.Gravida,
            para: response.para || response.Para,
            allergies: response.allergies || response.Allergies || '',
            clientType: response.clientType || response.ClientType,
            clientStatus: response.clientStatus || response.ClientStatus,
            user: { id: response.userId || response.User?.Id || null },
            midwife: { id: response.midwifeId || response.Midwife?.Id || null },
            // Preserve existing collections if not in response
            babies: response.Babies || client.babies || [],
            chartEntries: response.ChartEntries || client.chartEntries || [],
            pregnancies: response.Pregnancies || client.pregnancies || []
          };
          setClient(formattedResponse);
        }
        setSaveStatus("saved");
        setIsDirty(false);
        showToast("Changes saved successfully");
      } catch (err) {
        console.error('Auto-save error:', err);
        setSaveStatus("error");
        showToast("Failed to save changes", "error");
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [client, isDirty, clientId, showToast]);

  const handleCreateNewPregnancy = async () => {
    if (!newPregnancyForm.dueDate) {
      showToast("Due date is required", "error");
      return;
    }

    try {
      const pregnancyData = {
        dueDate: newPregnancyForm.dueDate,
        notes: newPregnancyForm.notes,
        isTwins: newPregnancyForm.isTwins,
        gravida: parseInt(newPregnancyForm.gravida),
        para: parseInt(newPregnancyForm.para)
      };

      const newChart = await clientApi.addPregnancy(clientId, pregnancyData);
      setPregnancies(prev => [newChart, ...prev]);
      setSelectedPregnancy(newChart);
      setShowDueDateModal(false);
      setNewPregnancyForm({ 
        dueDate: "", 
        notes: "", 
        isTwins: false,
        gravida: client?.gravida || 0,
        para: client?.para || 0
      });
      showToast("New pregnancy record created");
    } catch (err) {
      console.error('Error creating pregnancy:', err);
      showToast(err.message || "Failed to create pregnancy chart", "error");
    }
  };

  const handleSaveBaby = async (babyData) => {
    try {
      setBabySaveStatus("saving");
      const savedBaby = await clientApi.addBaby(clientId, {
        ...babyData,
        isTwin: babyData.birthType === 'Twin',
        twinOrder: babyData.birthType === 'Twin' ? babies.length + 1 : null
      });
      
      if (babies.length > 0) {
        // Update existing baby in the array
        setBabies(prev => prev.map((baby, index) => 
          index === activeBabyIndex ? savedBaby : baby
        ));
        
        // If this is the first twin, automatically create a second twin form
        if (savedBaby.birthType === 'Twin') {
          const secondTwin = {
            isTwin: true,
            twinOrder: 2,
            birthType: 'Twin',
            dateOfBirth: savedBaby.dateOfBirth || "",
            birthPlace: savedBaby.birthPlace || "",
            lastName: savedBaby.lastName || ""
          };
          setBabies(prev => [...prev, secondTwin]);
        }
      } else {
        // Add new baby to the array
        setBabies(prev => [...prev, savedBaby]);
        
        // If this is the first twin, automatically create a second twin form
        if (savedBaby.birthType === 'Twin') {
          const secondTwin = {
            isTwin: true,
            twinOrder: 2,
            birthType: 'Twin',
            dateOfBirth: savedBaby.dateOfBirth || "",
            birthPlace: savedBaby.birthPlace || "",
            lastName: savedBaby.lastName || ""
          };
          setBabies(prev => [...prev, secondTwin]);
        }
      }
      
      setBabyForm({ 
        name: "", 
        birthWeight: "", 
        notes: "", 
        isTwin: false, 
        twinOrder: null,
        birthType: "Single"
      });
      setBabySaveStatus("saved");
      showToast("Baby information saved successfully");
    } catch (err) {
      setBabySaveStatus("error");
      setError("Failed to save baby info");
    }
  };

  const handleSendInvite = async () => {
    try {
      setInviteStatus("sending");
      await clientApi.sendPortalInvite(clientId);
      setInviteStatus("sent");
      showToast("Portal invite sent successfully");
    } catch (err) {
      setInviteStatus("error");
      setError("Failed to send portal invite");
    } finally {
      setTimeout(() => setInviteStatus("idle"), 3000);
    }
  };

  const handleVisitChange = (e) => {
    const { name, value } = e.target;
    setSelectedVisit(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveVisit = async (visitData) => {
    try {
      // Create a complete client object with all required fields
      const clientData = {
        Id: client.id,
        FirstName: client.firstName,
        LastName: client.lastName,
        User: {
          Id: client.user?.id,
          FirstName: client.firstName,  // Use client's name for user fields
          LastName: client.lastName,
          Email: client.email || `${client.firstName}.${client.lastName}@example.com`.toLowerCase(),
          PasswordHash: "placeholder"  // This is just a placeholder since we don't have access to the actual hash
        },
        Pregnancies: [],  // Empty arrays for collections
        Appointments: [],
        ChartEntries: []
      };

      const visitWithClientData = {
        ...visitData,
        pregnancy: {
          ...selectedPregnancy,
          Client: clientData
        }
      };

      const savedVisit = isNewVisit
        ? await clientApi.createVisit(selectedPregnancy.id, visitWithClientData)
        : await clientApi.updateVisit(selectedPregnancy.id, selectedVisit.id, visitWithClientData);
      
      if (isNewVisit) {
        setVisits(prev => [...prev, savedVisit]);
      } else {
        setVisits(prev => prev.map(v => v.id === savedVisit.id ? savedVisit : v));
      }
      
      setSelectedVisit(savedVisit);
      setIsNewVisit(false);
      showToast('Visit saved successfully');
      return true;
    } catch (err) {
      console.error('Error saving visit:', err);
      showToast('Error saving visit', 'error');
      return false;
    }
  };

  if (isLoading) return <div className="p-6 text-gray-600">Loading chart...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 text-gray-900">
      {toastMessage && <Toast message={toastMessage} onClose={() => showToast("")} />}
      
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h1 className="text-2xl font-bold text-emerald-700">Client Chart</h1>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={goToClients}
            className="text-sm bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded"
          >Back to Clients</button>
          <button
            onClick={goToDashboard}
            className="text-sm bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded"
          >Dashboard</button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-700">
          <div>
            <p className="text-gray-500">Full Name</p>
            <p className="font-medium">{client.firstName} {client.lastName}</p>
          </div>
          <div>
            <p className="text-gray-500">Date of Birth</p>
            <p>{client.dateOfBirth?.slice(0, 10)}</p>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm text-gray-700 font-medium mb-1">Pregnancy Chart</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <select
              className="border rounded px-2 py-1"
              value={selectedPregnancy?.id || ''}
              onChange={(e) => {
                const found = pregnancies.find(p => p.id === parseInt(e.target.value));
                setSelectedPregnancy(found);
              }}
            >
              {pregnancies.map(p => (
                <option key={p.id} value={p.id}>
                  Due: {p.dueDate.slice(0, 10)}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowDueDateModal(true)}
              className="text-sm bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700"
            >+ New Pregnancy</button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-2 mb-6 overflow-x-auto">
        <nav className="flex flex-wrap gap-2 w-full sm:w-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-4 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >{tab.label}</button>
          ))}
        </nav>
      </div>

      <div className="bg-white p-4 rounded-xl shadow">
        {activeTab === "contact" && client && (
          <ContactInfoForm
            client={client}
            onChange={handleClientChange}
            onSendInvite={handleSendInvite}
            onSave={saveClientChanges}
            saveStatus={saveStatus}
            inviteStatus={inviteStatus}
            />
        )}
        {activeTab === "summary" && (
          <div><p className="text-gray-700">Pregnancy Summary for chart #{selectedPregnancy?.id}</p></div>
        )}
        {activeTab === "vitals" && (
          <div><p className="text-gray-700">Vitals for chart #{selectedPregnancy?.id}</p></div>
        )}
        {activeTab === "visits" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Routine Prenatal Visits</h2>
              <button
                onClick={() => {
                  setSelectedVisit({});
                  setIsNewVisit(true);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm"
              >
                New Visit
              </button>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {/* Visits List */}
              <div className="col-span-1 bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="font-medium text-gray-700">Visit History</h3>
                </div>
                <div className="divide-y max-h-[calc(100vh-300px)] overflow-y-auto">
                  {visits.length === 0 ? (
                    <div className="p-4 text-gray-500 text-sm">No visits recorded</div>
                  ) : (
                    visits.map(visit => (
                      <button
                        key={visit.id}
                        onClick={() => {
                          setSelectedVisit(visit);
                          setIsNewVisit(false);
                        }}
                        className={`w-full text-left p-4 hover:bg-gray-50 ${
                          selectedVisit?.id === visit.id ? 'bg-gray-100' : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          Visit #{visit.visitNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(visit.visitDate).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {visit.gestAgeWeeks}w {visit.gestAgeDays}d
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Visit Form */}
              <div className="col-span-3">
                {(selectedVisit || isNewVisit) ? (
                  <PrenatalVisitForm
                    visit={selectedVisit}
                    onChange={handleVisitChange}
                    onSave={handleSaveVisit}
                    visitNumber={isNewVisit ? visits.length + 1 : selectedVisit.visitNumber}
                    gestationalAge={selectedPregnancy?.gestationalAge}
                    pregnancy={selectedPregnancy}
                  />
                ) : (
                  <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                    Select a visit from the list or create a new one
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {activeTab === "labs" && (
          <div><p className="text-gray-700">Labs for chart #{selectedPregnancy?.id}</p></div>
        )}
        {activeTab === "birth" && (
          <div className="space-y-4">
            {/* Birth Type Selection */}
            <div className="bg-white p-4 rounded-xl shadow mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Birth Type</h3>
              <div className="relative">
                <select
                  value={babies[activeBabyIndex]?.birthType || babyForm.birthType}
                  onChange={(e) => handleActiveBabyChange({ target: { name: 'birthType', value: e.target.value }})}
                  className={`w-48 appearance-none rounded-lg border-2 px-3 py-2 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                    (babies[activeBabyIndex]?.birthType || babyForm.birthType) !== 'Single'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <option value="Single">Single Birth</option>
                  <option value="Twin">Twin Birth</option>
                  <option value="Breach">Breach Birth</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Twin Management UI - Only show if birth type is Twin */}
            {babies[activeBabyIndex]?.birthType === 'Twin' && (
              <div className="bg-white p-4 rounded-xl shadow">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Twin Birth Events</h3>
                <div className="flex gap-2">
                  {babies.map((baby, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveBabyIndex(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        activeBabyIndex === index
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      Twin {baby.twinOrder} {baby.firstName ? `(${baby.firstName})` : ''}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {babies.length === 2 
                    ? "Enter birth details for each twin separately by selecting the corresponding twin above."
                    : "Complete and save Twin 1's information to proceed with Twin 2."}
                </p>
              </div>
            )}

            {/* Breach Birth Details - Only show if birth type is Breach */}
            {babies[activeBabyIndex]?.birthType === 'Breach' && (
              <div className="bg-white p-4 rounded-xl shadow">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Breach Birth Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1">Breach Type*</label>
                    <select
                      name="breachType"
                      value={babies[activeBabyIndex]?.breachType || ""}
                      onChange={handleActiveBabyChange}
                      required
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Select Type</option>
                      <option value="Frank">Frank (Complete)</option>
                      <option value="Complete">Complete (Flexed)</option>
                      <option value="Footling">Footling</option>
                      <option value="Kneeling">Kneeling</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1">Breach Extraction Method*</label>
                    <select
                      name="breachExtractionMethod"
                      value={babies[activeBabyIndex]?.breachExtractionMethod || ""}
                      onChange={handleActiveBabyChange}
                      required
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Select Method</option>
                      <option value="Spontaneous">Spontaneous</option>
                      <option value="Assisted">Assisted</option>
                      <option value="Total">Total Extraction</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1">Head Delivery Method*</label>
                    <select
                      name="breachHeadDelivery"
                      value={babies[activeBabyIndex]?.breachHeadDelivery || ""}
                      onChange={handleActiveBabyChange}
                      required
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Select Method</option>
                      <option value="Spontaneous">Spontaneous</option>
                      <option value="MSV">Mauriceau-Smellie-Veit</option>
                      <option value="Forceps">Forceps</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1">Complications</label>
                    <select
                      name="breachComplications"
                      value={babies[activeBabyIndex]?.breachComplications || ""}
                      onChange={handleActiveBabyChange}
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                    >
                      <option value="">Select Complication</option>
                      <option value="None">None</option>
                      <option value="ExtendedArms">Extended Arms</option>
                      <option value="ExtendedHead">Extended Head</option>
                      <option value="NuchalArms">Nuchal Arms</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block mb-1">Additional Notes</label>
                    <textarea
                      name="breachNotes"
                      value={babies[activeBabyIndex]?.breachNotes || ""}
                      onChange={handleActiveBabyChange}
                      className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                      rows={3}
                      placeholder="Enter any additional notes about the breach birth..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Birth Event Form */}
            <BirthEventForm
              baby={babies[activeBabyIndex] || babyForm}
              onChange={handleActiveBabyChange}
              onSave={handleSaveBaby}
              saveStatus={babySaveStatus}
            />
          </div>
        )}
      </div>

      {showDueDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">New Pregnancy Chart</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date*</label>
                <input
                  type="date"
                  name="dueDate"
                  value={newPregnancyForm.dueDate}
                  onChange={(e) => handlePregnancyChange({
                    target: {
                      name: 'dueDate',
                      value: e.target.value
                    }
                  })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gravida*</label>
                  <input
                    type="number"
                    name="gravida"
                    min="0"
                    value={newPregnancyForm.gravida}
                    onChange={handlePregnancyChange}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Para*</label>
                  <input
                    type="number"
                    name="para"
                    min="0"
                    value={newPregnancyForm.para}
                    onChange={handlePregnancyChange}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Is Twin Pregnancy?</label>
                <div className="flex items-center space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="isTwins"
                      value="true"
                      checked={newPregnancyForm.isTwins === true}
                      onChange={(e) => handlePregnancyChange({
                        target: {
                          name: 'isTwins',
                          value: e.target.value === 'true'
                        }
                      })}
                      className="form-radio text-emerald-600"
                    />
                    <span className="ml-2">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="isTwins"
                      value="false"
                      checked={newPregnancyForm.isTwins === false}
                      onChange={(e) => handlePregnancyChange({
                        target: {
                          name: 'isTwins',
                          value: e.target.value === 'true'
                        }
                      })}
                      className="form-radio text-emerald-600"
                    />
                    <span className="ml-2">No</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={newPregnancyForm.notes}
                  onChange={handlePregnancyChange}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  placeholder="Add any initial notes..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowDueDateModal(false);
                  setNewPregnancyForm({ 
                    dueDate: "", 
                    notes: "", 
                    isTwins: false,
                    gravida: client?.gravida || 0,
                    para: client?.para || 0
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400/50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewPregnancy}
                disabled={!newPregnancyForm.dueDate}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                  newPregnancyForm.dueDate
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-emerald-400 cursor-not-allowed'
                }`}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
